import React from 'react';
import { Box, Button, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { InpatientRequestResponseDto } from 'src/api/surgery/surgeryServiceDtos';
import { FlowStep } from '../helpers';

interface Props {
  t: (key: string) => string;
  selectedSurgery: InpatientRequestResponseDto | null;
  loading: boolean;
  operationsLoading: boolean;
  processHistoryLoading: boolean;
  surgeryFlowLoading: boolean;
  surgeryFlowState: number;
  processSteps: FlowStep[];
  postSurgerySteps: FlowStep[];
  getStatusStepNumber: (statusId: number) => number;
  getLastStatusDateTime: (statusId: number) => string;
  onFlowStepClick: (stepId: number) => void;
  onRequestConfirmAction: (action: { type: 'ertelendi' | 'iptal'; stepId: number }) => void;
}

export function OperatingRoomFlowSection({
  t,
  selectedSurgery,
  loading,
  operationsLoading,
  processHistoryLoading,
  surgeryFlowLoading,
  surgeryFlowState,
  processSteps,
  postSurgerySteps,
  getStatusStepNumber,
  getLastStatusDateTime,
  onFlowStepClick,
  onRequestConfirmAction,
}: Props) {
  const isInteractive = Boolean(selectedSurgery && !loading);

  const isPrimaryStepDisabled = (buttonStepId: number) => {
    if (surgeryFlowLoading) return true;
    if (!selectedSurgery) return true;

    const currentStatusId = selectedSurgery.statusid || 0;
    const blockedStatuses = new Set([1695, 1690, 1650, 1655]);

    if (blockedStatuses.has(currentStatusId)) return true;
    if (currentStatusId === 1660 && buttonStepId >= 1670) return true;
    if (buttonStepId <= currentStatusId) return true;
    if (currentStatusId === 1705 || currentStatusId === 1708) return true;

    const nextStepMap: Record<number, number[]> = {
      1665: [1670],
      1670: [1675],
      1675: [1677],
      1677: [1680],
      1680: [1685],
      1685: [1687],
      1687: [1700, 1705, 1708],
    };

    const allowedNextSteps = nextStepMap[currentStatusId];
    if (!allowedNextSteps) return false;

    return !allowedNextSteps.includes(buttonStepId);
  };

  const isSecondaryActionDisabled = () => {
    if (!selectedSurgery || surgeryFlowLoading) return true;
    return new Set([1705, 1708, 1715, 1650, 1660, 1670, 1675, 1685, 1687, 1700, 1690, 1695, 1655, 1750]).has(
      selectedSurgery.statusid || 0
    );
  };

  const isPostStepDisabled = (buttonStepId: number) => {
    if (surgeryFlowLoading || !selectedSurgery) return true;

    const currentStatusId = selectedSurgery.statusid || 0;
    if (currentStatusId === 1715 || currentStatusId === 1750) return true;
    if (currentStatusId < 1687) return true;

    if (currentStatusId === 1690 || currentStatusId === 1695) {
      return buttonStepId !== 1705;
    }

    if (buttonStepId === 1705 && currentStatusId === 1708) return true;
    if (buttonStepId === 1708 && currentStatusId === 1705) return true;

    return false;
  };

  const loadingOverlay = (operationsLoading || processHistoryLoading) && (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" color="textSecondary">
        {t('surgery.messages.loading')}
      </Typography>
    </Box>
  );

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">{t('surgery.flow.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!selectedSurgery && (
            <Chip label={t('surgery.flow.selectPatient')} color="warning" size="small" sx={{ fontSize: '0.75rem' }} />
          )}
          <Chip
            label={`${t('surgery.flow.status')}: ${getStatusStepNumber(selectedSurgery?.statusid || 0)}/9`}
            color="primary"
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'center',
          opacity: isInteractive ? 1 : 0.5,
          pointerEvents: isInteractive ? 'auto' : 'none',
          position: 'relative',
          mt: 2,
          backgroundColor: '#fafbfc',
          borderRadius: 2,
          p: 1.5,
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        {loadingOverlay}
        {processSteps.map((step, index) => (
          <Box key={step.id} sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={getLastStatusDateTime(step.id) || step.name} arrow placement="top">
              <span>
                <Button
                  variant={
                    selectedSurgery?.statusid === 1715
                      ? step.id === 1650
                        ? 'contained'
                        : 'text'
                      : step.active || step.id === selectedSurgery?.statusid
                        ? 'contained'
                        : step.completed
                          ? 'outlined'
                          : 'text'
                  }
                  color={
                    selectedSurgery?.statusid === 1715
                      ? step.id === 1650
                        ? 'primary'
                        : 'inherit'
                      : step.active || step.id === selectedSurgery?.statusid
                        ? 'primary'
                        : step.completed
                          ? 'success'
                          : 'inherit'
                  }
                  size="small"
                  onClick={() => {
                    if (selectedSurgery?.statusid === 1715) {
                      if (step.id === 1650) onFlowStepClick(step.id);
                      return;
                    }

                    if (!isPrimaryStepDisabled(step.id)) {
                      onFlowStepClick(step.id);
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    minWidth: '120px',
                    height: '50px',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-line',
                    lineHeight: '1.2',
                    cursor: selectedSurgery?.statusid === 1715 ? (step.id === 1650 ? 'pointer' : 'not-allowed') : isPrimaryStepDisabled(step.id) ? 'not-allowed' : 'pointer',
                    ...(surgeryFlowLoading && step.id === surgeryFlowState
                      ? {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          animation: 'pulse 0.75s ease-in-out infinite',
                          '&:hover': { backgroundColor: 'primary.dark' },
                        }
                      : {}),
                  }}
                >
                  {step.name}
                </Button>
              </span>
            </Tooltip>
            {index < processSteps.length - 1 && (
              <Box
                sx={{
                  width: '20px',
                  height: '2px',
                  backgroundColor: step.completed ? '#4caf50' : '#e0e0e0',
                  mx: 1,
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'stretch',
          pr: 1,
          opacity: isInteractive ? 1 : 0.5,
          pointerEvents: isInteractive ? 'auto' : 'none',
        }}
      >
        <Box
          sx={{
            flex: '0 0 40%',
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 1.5,
            border: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          {loadingOverlay}
          <Tooltip title={getLastStatusDateTime(1690) || t('surgery.flow.postponed')} arrow placement="top">
            <span>
              <Button
                variant={selectedSurgery?.statusid === 1690 ? 'contained' : 'outlined'}
                color="warning"
                size="small"
                onClick={() => {
                  if (!isSecondaryActionDisabled()) {
                    onRequestConfirmAction({ type: 'ertelendi', stepId: 1690 });
                  }
                }}
                sx={{
                  textTransform: 'none',
                  minWidth: '120px',
                  height: '45px',
                  fontSize: '0.8rem',
                  borderWidth: 2,
                  fontWeight: 500,
                  cursor: isSecondaryActionDisabled() ? 'not-allowed' : 'pointer',
                  opacity: isSecondaryActionDisabled() ? 0.5 : 1,
                  '&:hover': { borderWidth: 2 },
                }}
              >
                {t('surgery.flow.postponed').replace(' ', '\n')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={getLastStatusDateTime(1695) || t('surgery.flow.cancelled')} arrow placement="top">
            <span>
              <Button
                variant={selectedSurgery?.statusid === 1695 ? 'contained' : 'outlined'}
                color="error"
                size="small"
                onClick={() => {
                  if (!isSecondaryActionDisabled()) {
                    onRequestConfirmAction({ type: 'iptal', stepId: 1695 });
                  }
                }}
                sx={{
                  textTransform: 'none',
                  minWidth: '120px',
                  height: '45px',
                  fontSize: '0.8rem',
                  borderWidth: 2,
                  fontWeight: 500,
                  cursor: isSecondaryActionDisabled() ? 'not-allowed' : 'pointer',
                  opacity: isSecondaryActionDisabled() ? 0.5 : 1,
                  '&:hover': { borderWidth: 2 },
                }}
              >
                {t('surgery.flow.cancelled').replace(' ', '\n')}
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: '0 0 60%',
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            backgroundColor: '#f0f8ff',
            borderRadius: 2,
            p: 1.5,
            border: '1px solid rgba(25, 118, 210, 0.2)',
          }}
        >
          {loadingOverlay}
          {postSurgerySteps.map((step, index) => (
            <Box key={step.id} sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={getLastStatusDateTime(step.id) || step.name} arrow placement="top">
                <div>
                  <Button
                    variant={step.active || step.id === selectedSurgery?.statusid ? 'contained' : step.completed ? 'outlined' : 'text'}
                    color={step.active || step.id === selectedSurgery?.statusid ? 'primary' : step.completed ? 'success' : 'inherit'}
                    size="small"
                    disabled={isPostStepDisabled(step.id)}
                    onClick={() => {
                      if (step.id === selectedSurgery?.statusid) return;
                      if (step.id === 1700 && (selectedSurgery?.statusid === 1705 || selectedSurgery?.statusid === 1708)) return;
                      onFlowStepClick(step.id);
                    }}
                    sx={{
                      textTransform: 'none',
                      minWidth: '120px',
                      height: '50px',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-line',
                      lineHeight: '1.2',
                      cursor:
                        step.id === selectedSurgery?.statusid ||
                        (step.id === 1700 && (selectedSurgery?.statusid === 1705 || selectedSurgery?.statusid === 1708))
                          ? 'not-allowed'
                          : 'pointer',
                      ...(surgeryFlowLoading && step.id === surgeryFlowState
                        ? {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            animation: 'pulse 0.75s ease-in-out infinite',
                            '&:hover': { backgroundColor: 'primary.dark' },
                          }
                        : {}),
                    }}
                  >
                    {step.name}
                  </Button>
                </div>
              </Tooltip>
              {index < postSurgerySteps.length - 1 && (
                <Box
                  sx={{
                    width: '20px',
                    height: '2px',
                    backgroundColor: step.completed ? '#4caf50' : '#e0e0e0',
                    mx: 1,
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}
